import React, { useEffect, useState, useCallback } from "react";
import Container from "react-bootstrap/Container";
import Card from "react-bootstrap/Card";
import Badge from "react-bootstrap/Badge";
import Form from "react-bootstrap/Form";
import Spinner from "react-bootstrap/Spinner";
import Swal from "sweetalert2";
import Header from "../../components/protected/Header";
import Footer from "../../components/footer";
import { auth } from "../../config/firebase";
import { useTranslations } from "../../translations";
import { useIsAdmin } from "../../hooks/useIsAdmin";

const API_BASE = (
  process.env.REACT_APP_BLOOMS_API_URL || "https://ai-blooms.vercel.app/api/openai/blooms"
).replace(/\/api\/.*/, "");

const STATUS_VARIANT = { new: "warning", contacted: "info", converted: "success", discarded: "secondary" };

const AdminLeads = () => {
  const { t } = useTranslations();
  const { isAdmin, loading: adminLoading } = useIsAdmin();
  const [status, setStatus] = useState("new");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);

  const authHeaders = useCallback(async () => {
    const idToken = auth.currentUser ? await auth.currentUser.getIdToken(true) : null;
    return { "Content-Type": "application/json", Authorization: `Bearer ${idToken}` };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/leads?status=${status}`, { headers: await authHeaders() });
      const data = await res.json();
      setLeads(data.leads || []);
    } catch (e) {
      /* noop */
    } finally {
      setLoading(false);
    }
  }, [status, authHeaders]);

  useEffect(() => {
    if (!adminLoading && isAdmin) load();
  }, [adminLoading, isAdmin, load]);

  const setLeadStatus = async (leadId, newStatus) => {
    try {
      setActing(leadId);
      const res = await fetch(`${API_BASE}/api/admin/leads`, {
        method: "POST",
        headers: await authHeaders(),
        body: JSON.stringify({ leadId, status: newStatus }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Error");
      setLeads((prev) => prev.filter((l) => l.id !== leadId));
    } catch (err) {
      Swal.fire({ icon: "error", title: t("errorTitle"), text: err.message });
    } finally {
      setActing(null);
    }
  };

  return (
    <div className="dashboard-wrapper">
      <Header />
      <main className="dashboard-main-content pb-5">
        <Container fluid className="py-4 mx-auto" style={{ maxWidth: 960 }}>
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
            <h2 className="mb-0">{t("adminLeadsTitle")}</h2>
            {isAdmin && (
              <Form.Select style={{ maxWidth: 220 }} value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="new">{t("leadNew")}</option>
                <option value="contacted">{t("leadContacted")}</option>
                <option value="converted">{t("leadConverted")}</option>
                <option value="discarded">{t("leadDiscarded")}</option>
              </Form.Select>
            )}
          </div>

          {adminLoading ? (
            <div className="text-center py-5"><Spinner animation="border" /></div>
          ) : !isAdmin ? (
            <Card className="text-center p-4 border-0 shadow-sm" style={{ borderRadius: 16 }}>
              <Card.Body className="text-muted">{t("notAuthorized")}</Card.Body>
            </Card>
          ) : loading ? (
            <div className="text-center py-5"><Spinner animation="border" /></div>
          ) : leads.length === 0 ? (
            <Card className="text-center p-4 border-0 shadow-sm" style={{ borderRadius: 16 }}>
              <Card.Body className="text-muted">{t("noLeads")}</Card.Body>
            </Card>
          ) : (
            leads.map((l) => (
              <Card key={l.id} className="mb-3 border-0 shadow-sm" style={{ borderRadius: 14 }}>
                <Card.Body>
                  <div className="d-flex flex-wrap justify-content-between align-items-start gap-2">
                    <div>
                      <div className="fw-semibold">{l.institution}</div>
                      <div className="small text-muted">
                        {l.contactName} · {l.email}{l.phone ? ` · ${l.phone}` : ""}
                      </div>
                      <div className="small mt-1">
                        {l.numTeachers != null && <span className="me-3">👩‍🏫 {l.numTeachers}</span>}
                        {l.numStudents != null && <span>🎓 {l.numStudents}</span>}
                      </div>
                      {l.message && <div className="small text-muted mt-1 fst-italic">"{l.message}"</div>}
                    </div>
                    <Badge bg={STATUS_VARIANT[l.status] || "light"}>{l.status}</Badge>
                  </div>

                  <div className="d-flex flex-wrap gap-2 mt-3">
                    <a href={`mailto:${l.email}`} className="ld-btn ld-btn-ghost">✉️ {t("leadEmail")}</a>
                    {l.status !== "contacted" && (
                      <button className="ld-btn ld-btn-primary" disabled={acting === l.id} onClick={() => setLeadStatus(l.id, "contacted")}>
                        {acting === l.id ? <Spinner animation="border" size="sm" /> : t("markContacted")}
                      </button>
                    )}
                    {l.status !== "converted" && (
                      <button className="ld-btn ld-btn-success" disabled={acting === l.id} onClick={() => setLeadStatus(l.id, "converted")}>{t("markConverted")}</button>
                    )}
                    <button className="ld-btn ld-btn-ghost" disabled={acting === l.id} onClick={() => setLeadStatus(l.id, "discarded")}>{t("markDiscarded")}</button>
                  </div>
                </Card.Body>
              </Card>
            ))
          )}
        </Container>
        <Footer logged={true} />
      </main>

      <style>{`
        .ld-btn { display:inline-flex; align-items:center; gap:6px; height:auto !important; width:auto !important;
          padding:8px 16px; border-radius:10px; font-size:.875rem; font-weight:600; border:1.5px solid transparent;
          cursor:pointer; text-decoration:none; }
        .ld-btn:disabled { opacity:.6; }
        .ld-btn-primary { background:#0984e3; color:#fff; }
        .ld-btn-success { background:#00b894; color:#fff; }
        .ld-btn-ghost { background:#f4f4f6; color:#444; }
      `}</style>
    </div>
  );
};

export default AdminLeads;
